// src/hooks/useChildren.ts
// TanStack Query hooks for children CRUD operations

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import apiClient from '@/core/lib/api';
import type {
  Child,
  ChildListResponse,
  ChildListParams,
  ChildCreateRequest,
  ChildUpdateRequest,
  ChildDiagnosis,
} from '@/features/children/types/childTypes';

// ============================================================================
// Query Keys
// ============================================================================

export const childrenKeys = {
  all: ['children'] as const,
  lists: () => [...childrenKeys.all, 'list'] as const,
  list: (familyId: string, params?: ChildListParams) =>
    [...childrenKeys.lists(), familyId, params] as const,
  details: () => [...childrenKeys.all, 'detail'] as const,
  detail: (childId: string) => [...childrenKeys.details(), childId] as const,
  diagnoses: (childId: string) => [...childrenKeys.detail(childId), 'diagnoses'] as const,
  medications: (childId: string) => [...childrenKeys.detail(childId), 'medications'] as const,
  search: (familyId: string, query: string) =>
    [...childrenKeys.all, 'search', familyId, query] as const,
};

// ============================================================================
// API Functions
// ============================================================================

async function fetchChildren(
  familyId: string,
  params?: ChildListParams
): Promise<ChildListResponse> {
  const response = await apiClient.get(`/families/${familyId}/children`, { params });
  return response.data;
}

async function fetchChild(childId: string): Promise<Child> {
  const response = await apiClient.get(`/children/${childId}`);
  return response.data;
}

async function createChild(familyId: string, data: ChildCreateRequest): Promise<Child> {
  const response = await apiClient.post(`/families/${familyId}/children`, data);
  return response.data;
}

async function updateChild(childId: string, data: ChildUpdateRequest): Promise<Child> {
  const response = await apiClient.patch(`/children/${childId}`, data);
  return response.data;
}

async function deleteChild(childId: string): Promise<void> {
  await apiClient.delete(`/children/${childId}`);
}

async function uploadChildAvatar(childId: string, file: File): Promise<{ avatar_url: string }> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post(`/children/${childId}/avatar`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

async function searchChildren(
  familyId: string,
  query: string
): Promise<Pick<Child, 'id' | 'first_name' | 'last_name' | 'avatar_url'>[]> {
  const response = await apiClient.get(`/families/${familyId}/children/search`, {
    params: { q: query, limit: 10 },
  });
  return response.data;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch paginated list of children for a family
 */
export function useChildren(familyId: string, params?: ChildListParams) {
  return useQuery({
    queryKey: childrenKeys.list(familyId, params),
    queryFn: () => fetchChildren(familyId, params),
    enabled: !!familyId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Fetch infinite list of children (for scroll loading)
 */
export function useChildrenInfinite(familyId: string, params?: Omit<ChildListParams, 'page'>) {
  return useInfiniteQuery({
    queryKey: [...childrenKeys.list(familyId, params), 'infinite'],
    queryFn: ({ pageParam = 1 }) =>
      fetchChildren(familyId, { ...params, page: pageParam }),
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.total_pages ? lastPage.page + 1 : undefined,
    initialPageParam: 1,
    enabled: !!familyId,
  });
}

/**
 * Fetch single child by ID
 */
export function useChild(childId: string) {
  return useQuery({
    queryKey: childrenKeys.detail(childId),
    queryFn: () => fetchChild(childId),
    enabled: !!childId,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Create a new child
 */
export function useCreateChild(familyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ChildCreateRequest) => createChild(familyId, data),
    onSuccess: (newChild) => {
      // Invalidate children list
      queryClient.invalidateQueries({ queryKey: childrenKeys.lists() });
      // Add to cache
      queryClient.setQueryData(childrenKeys.detail(newChild.id), newChild);
    },
  });
}

/**
 * Update an existing child
 */
export function useUpdateChild(childId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ChildUpdateRequest) => updateChild(childId, data),
    onSuccess: (updatedChild) => {
      // Update detail cache
      queryClient.setQueryData(childrenKeys.detail(childId), updatedChild);
      // Invalidate list
      queryClient.invalidateQueries({ queryKey: childrenKeys.lists() });
    },
    onMutate: async (newData) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: childrenKeys.detail(childId) });
      
      // Snapshot previous value
      const previousChild = queryClient.getQueryData<Child>(childrenKeys.detail(childId));
      
      // Optimistically update
      if (previousChild) {
        queryClient.setQueryData(childrenKeys.detail(childId), {
          ...previousChild,
          ...newData,
        });
      }
      
      return { previousChild };
    },
    onError: (_err, _newData, context) => {
      // Rollback on error
      if (context?.previousChild) {
        queryClient.setQueryData(childrenKeys.detail(childId), context.previousChild);
      }
    },
  });
}

/**
 * Delete a child
 */
export function useDeleteChild() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteChild,
    onSuccess: (_data, childId) => {
      // Remove from detail cache
      queryClient.removeQueries({ queryKey: childrenKeys.detail(childId) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: childrenKeys.lists() });
    },
  });
}

/**
 * Upload child avatar
 */
export function useUploadChildAvatar(childId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => uploadChildAvatar(childId, file),
    onSuccess: (data) => {
      // Update the child's avatar in cache
      const currentChild = queryClient.getQueryData<Child>(childrenKeys.detail(childId));
      if (currentChild) {
        queryClient.setQueryData(childrenKeys.detail(childId), {
          ...currentChild,
          avatar_url: data.avatar_url,
        });
      }
      // Invalidate lists to update grid view
      queryClient.invalidateQueries({ queryKey: childrenKeys.lists() });
    },
  });
}

/**
 * Search children by name (autocomplete)
 */
export function useSearchChildren(familyId: string, query: string) {
  return useQuery({
    queryKey: childrenKeys.search(familyId, query),
    queryFn: () => searchChildren(familyId, query),
    enabled: !!familyId && query.length >= 2,
    staleTime: 10 * 1000, // 10 seconds
  });
}

// ============================================================================
// Diagnosis Hooks
// ============================================================================

async function fetchChildDiagnoses(childId: string): Promise<ChildDiagnosis[]> {
  const response = await apiClient.get(`/children/${childId}/diagnoses`);
  return response.data;
}

async function addChildDiagnosis(
  childId: string,
  data: {
    diagnosis_id: string;
    diagnosed_date?: string;
    diagnosed_by?: string;
    severity?: string;
    is_primary?: boolean;
    notes?: string;
  }
): Promise<ChildDiagnosis> {
  const response = await apiClient.post(`/children/${childId}/diagnoses`, data);
  return response.data;
}

async function removeChildDiagnosis(childId: string, diagnosisId: string): Promise<void> {
  await apiClient.delete(`/children/${childId}/diagnoses/${diagnosisId}`);
}

/**
 * Fetch diagnoses for a child
 */
export function useChildDiagnoses(childId: string) {
  return useQuery({
    queryKey: childrenKeys.diagnoses(childId),
    queryFn: () => fetchChildDiagnoses(childId),
    enabled: !!childId,
  });
}

/**
 * Add diagnosis to child
 */
export function useAddChildDiagnosis(childId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof addChildDiagnosis>[1]) =>
      addChildDiagnosis(childId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: childrenKeys.diagnoses(childId) });
      queryClient.invalidateQueries({ queryKey: childrenKeys.detail(childId) });
      queryClient.invalidateQueries({ queryKey: childrenKeys.lists() });
    },
  });
}

/**
 * Remove diagnosis from child
 */
export function useRemoveChildDiagnosis(childId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (diagnosisId: string) => removeChildDiagnosis(childId, diagnosisId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: childrenKeys.diagnoses(childId) });
      queryClient.invalidateQueries({ queryKey: childrenKeys.detail(childId) });
      queryClient.invalidateQueries({ queryKey: childrenKeys.lists() });
    },
  });
}

// ============================================================================
// Prefetch Functions
// ============================================================================

/**
 * Prefetch children list (for navigation)
 */
export function usePrefetchChildren(queryClient: ReturnType<typeof useQueryClient>) {
  return (familyId: string, params?: ChildListParams) => {
    queryClient.prefetchQuery({
      queryKey: childrenKeys.list(familyId, params),
      queryFn: () => fetchChildren(familyId, params),
      staleTime: 30 * 1000,
    });
  };
}

/**
 * Prefetch single child (for card hover)
 */
export function usePrefetchChild(queryClient: ReturnType<typeof useQueryClient>) {
  return (childId: string) => {
    queryClient.prefetchQuery({
      queryKey: childrenKeys.detail(childId),
      queryFn: () => fetchChild(childId),
      staleTime: 60 * 1000,
    });
  };
}


